import { GlobalConstants } from 'core/constants';
import { Globals } from 'core/globals';
import { Utils } from 'core/utils';
import { TextConstants } from './text.constants';
import { ProblemPosition, TextControllerMethods, TextEditingRefusalReason
       } from './text.controller.methods';
import { FontDescription, Text, TextAlignment, TextArea, TextChunk, TextColor
       } from './text.model';

export enum TextApplianceResult
{
  Success,
  Failure
}

export interface TextEditingResult
{
  type: TextApplianceResult;
  model: Text;
  reason: TextEditingRefusalReason;
  position?: ProblemPosition;
}

export interface ControllerOptions
{
  minFontSize?: number;
  maxFontSize?: number;
  useAvailableArea?: boolean;
}

export interface NormalizedControllerOptions
{
  minFontSize: number;
  maxFontSize: number;
  useAvailableArea: boolean;
}

export function normalizedOptions(
  options: ControllerOptions | undefined): NormalizedControllerOptions
{
  const normalized = {
    minFontSize: TextConstants.MIN_FONT_SIZE,
    maxFontSize: TextConstants.MAX_FONT_SIZE,
    useAvailableArea: false,
    ...(options || {})
  };

  if (Globals.mode === GlobalConstants.MODE_GOD) {
    return {
      ...normalized,
      minFontSize: 0,
      maxFontSize: Number.MAX_SAFE_INTEGER
    };
  } else {
    return normalized;
  }
}

export interface TextController
{
  setAvailableArea(model: Text, size: TextArea): TextEditingResult;
  setText(model: Text, newText: Array<TextChunk>, options?: ControllerOptions):
      TextEditingResult;
  setFontSize(model: Text, fontSize: number, options?: ControllerOptions):
      TextEditingResult;
  setFont(
      model: Text, fontDescription: FontDescription, options?: ControllerOptions
    ): TextEditingResult;
  setColor(model: Text, color: TextColor): TextEditingResult;
  setHorizontalAlignment(model: Text, alignment: TextAlignment):
      TextEditingResult;
  setVerticalAlignment(model: Text, alignment: TextAlignment):
      TextEditingResult;
}

export const TextControllerBase: TextController = {
  setAvailableArea: (model: Text, size: TextArea): TextEditingResult => {
    let workingModel = Utils.deepCopy(model);
    workingModel.availableArea = size;
    if (TextControllerMethods.getUsageAvailableAreaComparison(workingModel) ===
        TextEditingRefusalReason.None)
    {
      return <TextEditingResult>  {
        type: TextApplianceResult.Success,
        model: workingModel
      };
    }

    return <TextEditingResult> {
      type: TextApplianceResult.Failure,
      reason: TextEditingRefusalReason.AreaTooSmall,
      model: model,
      position: TextControllerMethods.locateProblemPosition(
          model, TextEditingRefusalReason.AreaTooSmall)
    };
  },

  setColor: (model: Text, color: string): TextEditingResult => {
    model.color = color;

    return <TextEditingResult>  {
      type: TextApplianceResult.Success,
      model: model
    };
  },

  setHorizontalAlignment: (model: Text, alignment: TextAlignment):
      TextEditingResult => {
    model.horizontalAlignment = alignment;

    return <TextEditingResult>  {
      type: TextApplianceResult.Success,
      model: model
    };
  },

  setVerticalAlignment: (model: Text, alignment: TextAlignment):
      TextEditingResult => {
    model.verticalAlignment = alignment;

    return <TextEditingResult>  {
      type: TextApplianceResult.Success,
      model: model
    };
  },

  setText: (
    model: Text,
    newText: Array<TextChunk>,
    options?: ControllerOptions): TextEditingResult =>
  {
    throw new Error('setText must be implemented');
  },

  setFontSize: (
    model: Text,
    fontSize: number,
    options?: ControllerOptions): TextEditingResult =>
  {
    throw new Error('setFontSize must be implemented');
  },

  setFont: (
    model: Text,
    fontDescription: FontDescription,
    options?: ControllerOptions): TextEditingResult =>
  {
    throw new Error('setFontFamily must be implemented');
  }
};



// WEBPACK FOOTER //
// ./src/app/text/text.controller.ts